<template>
  <PartnerRegistrationFormLayout
    :title="t('partner.registration.form.step1.title')"
  >
    <template #content>
      <PartnerRegistrationFormCard
        aside-image="/_nuxt3-static/partner/partner-onboarding-form-1.jpg"
        :benefits="tm('partner.registration.form.step1.benefits')"
      >
        <template #form>
          <h3 class="partner-registration-form-step1__section-title">
            {{
              t('partner.registration.form.step1.section.generalInformation')
            }}
          </h3>

          <form
            class="partner-registration-form-step1__form"
            novalidate
            @submit.prevent="form.handleSubmit"
          >
            <div class="partner-registration-form-step1__grid">
              <TanStackFormInput
                :form="form"
                name="business_name"
                :label="t('partner.registration.form.step1.businessName')"
                type="text"
                autocomplete="organization"
                :required="true"
                :validators="fieldValidators.business_name"
              />
              <TanStackFormAddressAutocomplete
                :form="form"
                name="business_address"
                :label="t('partner.registration.form.step1.businessAddress')"
                autocomplete="street-address"
                :required="true"
                :show-parsed-data="true"
                :validators="fieldValidators.business_address as any"
                @address-selected="handleAddressSelected"
              />
              <TanStackFormInput
                :form="form"
                name="phone_number"
                :label="t('partner.registration.form.step1.phoneNumber')"
                type="tel"
                autocomplete="tel"
                :required="true"
                :validators="fieldValidators.phone_number"
              />
              <TanStackFormInput
                :form="form"
                name="website_instagram"
                :label="t('partner.registration.form.step1.websiteInstagram')"
                type="text"
                placeholder="https://"
                autocomplete="url"
                :required="true"
                :validators="fieldValidators.website_instagram"
              />
              <TanStackFormSelect
                :form="form"
                name="business_size"
                :label="t('partner.registration.form.step1.businessSize')"
                type="number"
                autocomplete="off"
              >
                <option value="">
                  {{ t('partner.registration.form.step1.selectOption') }}
                </option>
                <option value="1-5">
                  {{
                    t('partner.registration.form.step1.businessSizeOptions.1-5')
                  }}
                </option>
                <option value="5-25">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.5-25'
                    )
                  }}
                </option>
                <option value="25-50">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.25-50'
                    )
                  }}
                </option>
                <option value="50-100">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.50-100'
                    )
                  }}
                </option>
                <option value="100-500">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.100-500'
                    )
                  }}
                </option>
                <option value="500-1000">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.500-1000'
                    )
                  }}
                </option>
                <option value="1000+">
                  {{
                    t(
                      'partner.registration.form.step1.businessSizeOptions.1000+'
                    )
                  }}
                </option>
              </TanStackFormSelect>
              <TanStackFormSelect
                :form="form"
                name="annual_revenue_range"
                :label="t('partner.registration.form.step1.annualRevenueRange')"
              >
                <option value="">
                  {{ t('partner.registration.form.step1.selectOption') }}
                </option>
                <option value="0-10.000">
                  {{ t('partner.registration.form.step1.revenue.0-10000') }}
                </option>
                <option value="10.001-25.000">
                  {{ t('partner.registration.form.step1.revenue.10000-25000') }}
                </option>
                <option value="25.001-50.000">
                  {{ t('partner.registration.form.step1.revenue.25000-50000') }}
                </option>
                <option value="50.001-100.000">
                  {{
                    t('partner.registration.form.step1.revenue.50000-100000')
                  }}
                </option>
                <option value="100.001-250.000">
                  {{
                    t('partner.registration.form.step1.revenue.100000-250000')
                  }}
                </option>
                <option value="250.001-500.000">
                  {{
                    t('partner.registration.form.step1.revenue.250000-500000')
                  }}
                </option>
                <option value="500.001-1.000.000">
                  {{
                    t('partner.registration.form.step1.revenue.500000-1000000')
                  }}
                </option>
                <option value="+1.000.000">
                  {{ t('partner.registration.form.step1.revenue.1000000+') }}
                </option>
              </TanStackFormSelect>
              <div>
                <TanStackFormSelect
                  :form="form"
                  name="referral"
                  :label="t('partner.registration.form.step1.referral')"
                >
                  <option value="">
                    {{ t('partner.registration.form.step1.selectOption') }}
                  </option>
                  <option value="Instagram">
                    {{
                      t(
                        'partner.registration.form.step1.referralOptions.instagram'
                      )
                    }}
                  </option>
                  <option value="Facebook">
                    {{
                      t(
                        'partner.registration.form.step1.referralOptions.facebook'
                      )
                    }}
                  </option>
                  <option value="Ads">
                    {{
                      t('partner.registration.form.step1.referralOptions.ads')
                    }}
                  </option>
                  <option value="Google Search">
                    {{
                      t(
                        'partner.registration.form.step1.referralOptions.googleSearch'
                      )
                    }}
                  </option>
                  <option value="Referral">
                    {{
                      t(
                        'partner.registration.form.step1.referralOptions.referral'
                      )
                    }}
                  </option>
                  <option value="Other">
                    {{
                      t(
                        'partner.registration.form.step1.referralOptions.other'
                      )
                    }}
                  </option>
                </TanStackFormSelect>
                <TanStackFormInput
                  v-if="showReferralDetail"
                  :form="form"
                  name="referral_detail"
                  :label="t('partner.registration.form.step1.referralDetail')"
                  class="mt-4"
                  type="text"
                  autocomplete="off"
                />
              </div>
              <TanStackFormSelect
                :form="form"
                name="language"
                :label="t('partner.registration.form.step1.language')"
                :required="true"
                :validators="fieldValidators.language"
              >
                <option value="">
                  {{ t('partner.registration.form.step1.selectOption') }}
                </option>
                <option value="de">
                  {{ t('partner.registration.form.step1.languageOptions.de') }}
                </option>
                <option value="en">
                  {{ t('partner.registration.form.step1.languageOptions.en') }}
                </option>
              </TanStackFormSelect>
            </div>

            <PartnerRegistrationFormSubmitButton>
              <form.Subscribe>
                <template #default="{ isSubmitting }">
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    class="partner-registration-form-step1__submit-button"
                    :disabled="isSubmitting"
                  >
                    {{ t('partner.registration.form.step1.next') }}
                  </Button>
                </template>
              </form.Subscribe>
            </PartnerRegistrationFormSubmitButton>
          </form>
        </template>
      </PartnerRegistrationFormCard>
    </template>
  </PartnerRegistrationFormLayout>
</template>

<script setup lang="ts">
import { useForm } from '@tanstack/vue-form'
import { z } from 'zod'
import type { ParsedAddressData } from '~/components/common/tan-stack-form/TanStackFormAddressAutocomplete.vue'
import { usePartnerRegistration } from '~/composables/usePartnerRegistration'

export type PartnerRegistrationFormData = {
  business_name: string
  business_address: string
  phone_number: string
  website_instagram: string
  business_size: string
  annual_revenue_range: string
  referral: string
  referral_detail: string
  language: string
}

const { updateData } = usePartnerRegistration()
const addressData = ref<ParsedAddressData | null>(null)
const isAddressBeingSelected = ref(false)

function hasValidAddressData(): boolean {
  if (!addressData.value) return false
  const { street, street_number, locality, zipcode, country } =
    addressData.value
  return !!(street && street_number && locality && zipcode && country)
}

const props = defineProps<{
  initialValues?: Partial<PartnerRegistrationFormData>
}>()

const emit = defineEmits<{
  'update:formData': [value: PartnerRegistrationFormData]
  submit: [value: PartnerRegistrationFormData]
}>()

const { t, tm } = useI18n()

const benefits = computed(
  () =>
    tm('partner.registration.form.step1.benefits') as {
      title: string
      items: string[]
    }
)

const defaultValues: PartnerRegistrationFormData = {
  business_name: props.initialValues?.business_name || '',
  business_address: props.initialValues?.business_address || '',
  phone_number: props.initialValues?.phone_number || '',
  website_instagram: props.initialValues?.website_instagram || '',
  business_size: props.initialValues?.business_size || '',
  annual_revenue_range: props.initialValues?.annual_revenue_range || '',
  referral: props.initialValues?.referral || '',
  referral_detail: props.initialValues?.referral_detail || '',
  language: props.initialValues?.language || 'de',
}

const createPartnerSchema = (t: (key: string) => string) => {
  return z.object({
    business_name: z.string().min(1, t('validation.required')),
    business_address: z.string().min(1, t('validation.required')),
    phone_number: z.string().min(1, t('validation.required')),
    website_instagram: z.string().min(1, t('validation.required')),
    business_size: z.string(),
    annual_revenue_range: z.string(),
    referral: z.string(),
    referral_detail: z.string(),
    language: z.string().min(1, t('validation.required')),
  })
}

// Field-level validators for individual fields
const fieldValidators = {
  business_name: {
    onBlur: z.string().min(1, t('validation.required')),
  },
  business_address: {
    onBlur: ({ value }: { value: string }) => {
      if (!value) return t('validation.required')
      if (!hasValidAddressData()) {
        return { message: t('validation.selectAddressFromDropdown') }
      }
      return undefined
    },
    onChange: ({ value }: { value: string }) => {
      if (!value) return t('validation.required')
      if (!hasValidAddressData()) {
        return { message: t('validation.selectAddressFromDropdown') }
      }
      return undefined
    },
  },
  phone_number: {
    onBlur: z.string().min(1, t('validation.required')),
  },
  website_instagram: {
    onBlur: z.string().min(1, t('validation.required')),
  },
  language: {
    onBlur: z.string().min(1, t('validation.required')),
  },
}

const form = useForm({
  defaultValues,
  validators: {
    onSubmit: ({ value }) => {
      const schema = createPartnerSchema(t)
      const result = schema.safeParse(value)
      if (!result.success) {
        return result.error.errors[0]?.message
      }
      if (!hasValidAddressData()) {
        return {
          fields: {
            business_address: t('validation.selectAddressFromDropdown'),
          },
        }
      }
      return undefined
    },
  },
  onSubmit: ({ value }) => {
    const schema = createPartnerSchema(t)
    const result = schema.safeParse(value)
    if (!result.success || !hasValidAddressData()) {
      return
    }
    emit('submit', value as PartnerRegistrationFormData)
  },
})

const formStore = form.useStore()

const showReferralDetail = computed(() => {
  return formStore.value.values.referral === 'Other'
})

watch(
  () => formStore.value.values.referral,
  (referral) => {
    if (referral !== 'Other') {
      form.setFieldValue('referral_detail', '')
    }
  }
)

const handleAddressSelected = (data: ParsedAddressData) => {
  isAddressBeingSelected.value = true
  addressData.value = data
  updateData(data)
  nextTick(() => {
    isAddressBeingSelected.value = false
    form.validateField('business_address', 'change')
    form.validateField('business_address', 'blur')
  })
}

watch(
  () => formStore.value,
  (newValues) => {
    emit('update:formData', newValues.values)

    // Map form field names to backend property names
    updateData({
      legal_name: newValues.values.business_name,
      phone: newValues.values.phone_number,
      website: newValues.values.website_instagram,
      business_size: newValues.values.business_size,
      annual_revenue: newValues.values.annual_revenue_range,
      referral: newValues.values.referral,
      referral_detail: newValues.values.referral_detail,
      language: newValues.values.language, // Will be transformed to main_language on submit
    })
  },
  { deep: true }
)

watch(
  () => formStore.value.values.business_address,
  () => {
    if (!isAddressBeingSelected.value && addressData.value) {
      addressData.value = null
    }
  }
)

const validate = async () => {
  await form.handleSubmit()
  const schemaResult = createPartnerSchema(t).safeParse(formStore.value.values)
  if (!schemaResult.success) {
    return schemaResult
  }
  if (!hasValidAddressData()) {
    return {
      success: false,
      error: {
        issues: [
          {
            path: ['business_address'],
            message: t('validation.selectAddressFromDropdown'),
          },
        ],
      },
    }
  }
  return schemaResult
}

defineExpose({
  validate,
})
</script>

<style lang="postcss" scoped>
.partner-registration-form-step1__section-title {
  @apply text-lg font-semibold text-gray-900 mb-4;
}

.partner-registration-form-step1__form {
  @apply space-y-6;
}

.partner-registration-form-step1__grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6;
}

.partner-registration-form-step1__submit-button {
  @apply w-full min-w-[120px];
}
</style>
